import React from "react";
import { Play } from "lucide-react";
import OrganizationHero from "./OrganizationHero";
import { Carousel, ImageGallery, ImageModal, VideoModal } from "../ui";
import LazyImage from "../ui/LazyImage";
import { createBlurDataURL } from "../../utils/image.utils";
import placeholder from "../../assets/images/placeholder.webp";
import { FadeInUp, ScaleIn } from "../animations/AnimationComponents";
import { GradientOrb } from "../animations/BackgroundEffects";
import type { Video } from "../../legacy/types/legacyBusiness.types";
import { useI18n } from "../../hooks/useI18n";
import type { OrganizationPageData as BusinessPageData } from "../../presentation/organizations/organizationPage.types";
import { selectDeterministicItem } from "../../domain/shared/commerce.utils";

const GallerySection: React.FC<{ business: BusinessPageData }> = ({ business }) => {
  const { text, t } = useI18n();
  const [modalVideo, setModalVideo] = React.useState<Video | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = React.useState<number | null>(null);

  const heroImage = selectDeterministicItem(business.images, business.id)?.url
    ?? business.backgroundImage
    ?? business.logo;

  const galleryImages = business.images.map((image, index) => ({
    url: image.url,
    name: image.name || `${business.name} ${index + 1}`,
    caption: t(text.business.gallerySubtitle, { name: business.name }),
  }));

  return (
    <>
      <OrganizationHero
        name={t(text.business.galleryHero, { name: business.name })}
        description={text.business.gallerySubtitle}
        heroImage={heroImage}
        height="medium"
      />

      <section className="relative overflow-hidden bg-white py-16">
        <GradientOrb className="absolute right-10 top-20 -z-10" size="lg" color="gray" />
        <GradientOrb className="absolute bottom-10 left-20 -z-10" size="md" color="gray" />
        <div className="container mx-auto max-w-6xl px-4">
          <FadeInUp delay={0.1}>
            <div className="mb-12 text-center">
              <h2 className="mb-2 text-3xl font-bold text-gray-800">{text.business.offerTitle}</h2>
              <div className="mx-auto mb-6 h-1 w-20 bg-primary"></div>
              <p className="text-lg text-gray-600">{t(text.business.gallerySubtitle, { name: business.name })}</p>
            </div>
          </FadeInUp>

          {business.images.length > 0 && (
            <FadeInUp delay={0.2}>
              <div className="mb-16">
                <h3 className="mb-6 text-center text-xl font-semibold text-gray-700">{text.business.featuredImages}</h3>
                <Carousel images={business.images} />
              </div>
            </FadeInUp>
          )}

          {business.images.length > 0 ? (
            <FadeInUp delay={0.3}>
              <div className="mb-16">
                <h3 className="mb-6 text-center text-xl font-semibold text-gray-700">{text.business.fullGallery}</h3>
                <ImageGallery images={galleryImages} columns={3} gap={6} onImageClick={setSelectedImageIndex} />
              </div>
            </FadeInUp>
          ) : (
            <FadeInUp delay={0.3}>
              <div className="rounded-lg border border-gray-200 bg-gray-50 py-12 text-center">
                <div className="flex flex-col items-center space-y-4">
                  <svg className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-lg text-gray-500">{text.business.noImages}</p>
                  <p className="text-sm text-gray-400">{text.business.imagesSoon}</p>
                </div>
              </div>
            </FadeInUp>
          )}

          {business.videos && business.videos.length > 0 && (
            <FadeInUp delay={0.4}>
              <div className="mt-16">
                <div className="mb-12 text-center">
                  <h3 className="mb-4 text-2xl font-bold text-gray-800">{text.business.videos}</h3>
                  <p className="text-lg text-gray-600">{text.business.videosBody}</p>
                </div>

                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {business.videos.map((video, index) => (
                    <ScaleIn key={video.id} delay={0.2 + index * 0.1}>
                      <div
                        className="group cursor-pointer overflow-hidden rounded-lg border-2 border-transparent bg-white transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-2xl"
                        onClick={() => setModalVideo(video)}
                      >
                        <div className="relative aspect-video bg-gray-100">
                          <LazyImage
                            src={video.thumbnail || business.logo}
                            alt={video.name}
                            width="100%"
                            height="100%"
                            className="h-full w-full"
                            placeholder={createBlurDataURL(16, 9)}
                            fallbackSrc={business.logo}
                            objectFit="cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-all duration-300 group-hover:bg-black/50">
                            <div className="rounded-full bg-white/20 p-4 backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                              <Play className="h-12 w-12 text-white" fill="currentColor" />
                            </div>
                          </div>
                          {(video.url === placeholder || video.url.includes("placeholder")) && (
                            <div className="absolute right-2 top-2 rounded-full bg-yellow-500/90 px-2 py-1 text-xs text-white">
                              {text.business.comingSoon}
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                          <div className="absolute inset-0 flex items-end p-4">
                            <div className="text-white">
                              <h4 className="mb-1 text-lg font-semibold">{video.name}</h4>
                              {video.description && <p className="text-sm text-gray-200">{video.description}</p>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </ScaleIn>
                  ))}
                </div>
              </div>
            </FadeInUp>
          )}
        </div>

        <VideoModal isOpen={!!modalVideo} onClose={() => setModalVideo(null)} video={modalVideo} />
        <ImageModal
          isOpen={selectedImageIndex !== null}
          onClose={() => setSelectedImageIndex(null)}
          images={galleryImages}
          initialIndex={selectedImageIndex || 0}
        />
      </section>
    </>
  );
};

export default GallerySection;
